import { Button } from '@daily/shared/components/Button';
import { Card, CardContent } from '@daily/shared/components/Card';
import { ArchiveIcon } from '@daily/shared/components/Icons/ArchiveIcon';
import { Select } from '@daily/shared/components/Select';
import { Stack } from '@daily/shared/components/Stack';
import { Text } from '@daily/shared/components/Text';
import { VisuallyHidden } from '@daily/shared/components/VisuallyHidden';
import { useResizeObserver } from '@daily/shared/hooks/useResizeObserver';
import { useLocalSessionId } from '@daily-co/daily-react';
import React, { useCallback, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useBreakouts } from '/contexts/BreakoutsProvider';
import { useIsMobile } from '/lib/state/core';

import { MAIN_ROOM_IDENTIFIER } from './helpers/constants';
import { shuffleArray } from './helpers/lib';
import { useBreakoutSessionState } from './hooks/useBreakoutSessionState';
import { useRoomMapping } from './hooks/useRoomMapping';
import { useRooms } from './hooks/useRooms';
import { useSelectParticipant } from './hooks/useSelectParticipant';
import { Participant } from './Participant';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  onChangeSelect?(): void;
  onDragOver;
  onDrop;
  onResize?(): void;
  roomIdentifier: string;
  roomName?: string | number;
}

export const Room: React.FC<React.PropsWithChildren<Props>> = ({
  onChangeSelect,
  onDragOver,
  onDrop,
  onResize,
  roomIdentifier,
  roomName,
}) => {
  const { t } = useTranslation();
  const { deleteRoom, rooms } = useRooms();
  const { getRoomMappingByRoomId, updateRoomMapping } = useRoomMapping();
  const localSessionId = useLocalSessionId();
  const { moveParticipants } = useBreakouts();
  const {
    breakoutHost,
    breakoutStatus,
    invitedParticipants,
    mainRoomParticipantIds,
  } = useBreakoutSessionState();

  const { deselectParticipant, selectedParticipants } = useSelectParticipant();
  const [isMobile] = useIsMobile();

  const participantIds = getRoomMappingByRoomId(roomIdentifier);
  const isMainRoom = roomIdentifier === MAIN_ROOM_IDENTIFIER;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const initialParticipantsToRender = 12;

  const [participantsToLoad, setParticipantsToLoad] = useState(
    initialParticipantsToRender
  );
  const participantsLeftToLoad = participantIds?.length - participantsToLoad;

  const handleLoadAll = () => {
    setParticipantsToLoad(participantIds?.length);
  };

  const handleLoadLess = () => {
    setParticipantsToLoad(initialParticipantsToRender);
  };

  const handleDragOver = (ev: React.DragEvent<HTMLElement>) => {
    onDragOver?.(ev);
    ev.currentTarget.style.borderColor = 'var(--focus-border)';
  };

  const handleDragLeave = (ev: React.DragEvent<HTMLElement>) => {
    ev.currentTarget.style.borderColor = '';
  };

  const handleDrop = (ev: React.DragEvent<HTMLElement>) => {
    onDrop?.(ev);
    ev.currentTarget.style.borderColor = '';
  };

  const handleDeleteRoom = (room: string) => {
    participantIds?.map((participant) => {
      updateRoomMapping(participant, MAIN_ROOM_IDENTIFIER);
    });
    deleteRoom(room);
  };

  const joinOngoingSession = () => {
    if (breakoutStatus !== 'ongoing') return;
    updateRoomMapping(localSessionId, roomIdentifier);
    moveParticipants({
      [localSessionId]: isMainRoom ? null : roomIdentifier,
    });
  };

  const handleSelectRoomsChange = (roomIdentifier: string) => {
    // if this is triggered, the Ongoing Breakout Sessions panel
    // needs to know about it -- see `hasPendingChanges` there.
    // if the default "Select" option is chosen, don't try to assign the participant(s) to that value.
    if (!roomIdentifier) return;
    onChangeSelect?.();
    selectedParticipantsInRoom.map((participant) => {
      updateRoomMapping(participant, roomIdentifier);
      deselectParticipant(participant);
    });
  };

  /* Which participants in this current room are also selected? */
  const selectedParticipantsInRoom = selectedParticipants.filter(
    (participant) => participantIds?.includes(participant)
  );

  // Which main room participants are also selected? Needed for the random assignment button.
  const selectedParticipantsInMainRoom = selectedParticipants.filter(
    (participant) => mainRoomParticipantIds.includes(participant)
  );

  const participantCannotBeMoved = (p) => {
    // Host can always be moved
    const isHost = p === breakoutHost;
    const isInvited = invitedParticipants?.includes(p);
    return !isHost && isInvited && breakoutStatus === 'ongoing';
  };

  const randomlyAssignParticipants = () => {
    const ids =
      selectedParticipantsInMainRoom.length > 0
        ? [...selectedParticipantsInMainRoom] // making a copy
        : participantIds?.filter((id) => id !== localSessionId);
    const shuffledParticipants = shuffleArray(ids);

    for (let i = 0; i < shuffledParticipants.length; i++) {
      const roomId = rooms[i % rooms.length];
      updateRoomMapping(shuffledParticipants[i], roomId);
      // make sure to deselect any participants upon random room assignment
      deselectParticipant(shuffledParticipants[i]);
    }
  };

  const showRoomSelect =
    breakoutStatus !== 'ongoing' && isMainRoom
      ? true
      : selectedParticipantsInRoom.length > 0;

  // Options for the room dropdown menu
  const roomsOptions: React.ComponentProps<typeof Select>['options'] =
    useMemo(() => {
      const placeholder = {
        value: '',
        label: t('breakouts.selectRoomPlaceholder'),
      };
      const mainRoom = {
        value: MAIN_ROOM_IDENTIFIER,
        label: t('breakouts.mainRoom'),
      };
      const availableRooms = rooms
        .map((roomId) => ({
          value: roomId,
          label: t('breakouts.roomWithName', {
            name: rooms.indexOf(roomId) + 1,
          }),
        }))
        .filter((room) => room.value !== roomIdentifier);

      return isMainRoom
        ? [placeholder, ...availableRooms]
        : [placeholder, mainRoom, ...availableRooms];
    }, [isMainRoom, roomIdentifier, rooms, t]);

  // Only visible to the host when a breakout session is ongoing
  const showJoinRoomButton =
    breakoutStatus === 'ongoing' && !participantIds?.includes(localSessionId);

  // Only visible to the host when a breakout room is not ongoing
  const showDeleteRoomButton =
    breakoutStatus !== 'ongoing' && roomIdentifier !== MAIN_ROOM_IDENTIFIER;

  // Only render the random assignment button for the main room section
  const showRandomAssignmentButton = isMainRoom && breakoutStatus !== 'ongoing';

  // Disable random assignment button when only the host is left to assign
  const disableRandomAssignmentButton = !mainRoomParticipantIds.some(
    (id) => id !== localSessionId
  );

  const deselectAllParticipantsInRoom = () => {
    participantIds?.map((p) => deselectParticipant(p));
  };

  const showShowAllButton = participantIds?.length > participantsToLoad;
  const showShowLessButton = participantIds?.length === participantsToLoad;

  useResizeObserver(
    wrapperRef,
    useCallback(() => {
      if (!wrapperRef.current) return;
      onResize?.();
    }, [onResize])
  );

  const assignSelectId = useId();

  return (
    <Card
      data-room-identifier={roomIdentifier}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      shadow="none"
      spacing={12}
      className="room"
    >
      <div
        ref={wrapperRef}
        data-testid={`breakout-${roomName ?? MAIN_ROOM_IDENTIFIER}`}
      >
        <CardContent>
          <div className="header-container">
            <Text variant="base" El="h1">
              {isMainRoom
                ? t('breakouts.mainRoom')
                : t('breakouts.roomWithName', { name: roomName })}
            </Text>
            {showJoinRoomButton && (
              <Button
                id={`breakouts-join-room-${roomName}`}
                variant="secondary"
                size="small"
                onClick={joinOngoingSession}
              >
                {t('breakouts.joinRoom')}
              </Button>
            )}
            {showDeleteRoomButton && (
              <Button
                id={`breakouts-delete-room-${roomName}`}
                variant="ghost"
                disabled={rooms.length < 2} // there must be at least 1 room
                type="button"
                onClick={() => handleDeleteRoom(roomIdentifier)}
              >
                <ArchiveIcon size={16} />
                <VisuallyHidden>{t('breakouts.deleteRoom')}</VisuallyHidden>
              </Button>
            )}
          </div>

          <Stack gap={16}>
            <div className="room-container">
              <div className="buttons-container">
                <div className="select-buttons">
                  <Button
                    id={`breakouts-deselect-all-${roomName}`}
                    variant="ghost"
                    onClick={deselectAllParticipantsInRoom}
                    disabled={selectedParticipantsInRoom.length === 0}
                  >
                    {t('breakouts.deselectAll')}
                  </Button>
                </div>

                {showShowAllButton && (
                  <Button
                    id={`breakouts-show-all-${roomName}`}
                    variant="ghost"
                    onClick={handleLoadAll}
                  >
                    {t('general.showAll')}
                  </Button>
                )}
                {showShowLessButton && (
                  <Button
                    id={`breakouts-show-less-${roomName}`}
                    variant="ghost"
                    onClick={handleLoadLess}
                  >
                    {t('general.showLess')}
                  </Button>
                )}
              </div>

              <div className="participants-container">
                {participantIds?.length > 0 ? (
                  <>
                    {participantIds?.slice(0, participantsToLoad)?.map((p) => (
                      <Participant
                        participant={p}
                        key={p}
                        disabled={participantCannotBeMoved(p)}
                      />
                    ))}
                    {participantsLeftToLoad > 0 && (
                      <Text className="participants-left">
                        {t('breakouts.participantsWithCount', {
                          count: participantsLeftToLoad,
                        })}
                      </Text>
                    )}
                  </>
                ) : (
                  <Text color="muted">
                    {!isMobile && t('breakouts.dragParticipantsHere')}
                  </Text>
                )}
              </div>
            </div>

            {showRoomSelect && (
              <div className="dropdown">
                <Select
                  id={assignSelectId}
                  name="select-breakout-room"
                  label={t('breakouts.assignSelectedTo')}
                  disabled={selectedParticipantsInRoom.length === 0}
                  options={roomsOptions}
                  native
                  value="" // not assigning a stateful value since we want to reset to "" after a room has been selected
                  onChange={handleSelectRoomsChange}
                />
              </div>
            )}

            {showRandomAssignmentButton && (
              <Button
                id={`breakouts-assign-${roomName}`}
                disabled={disableRandomAssignmentButton}
                fullWidth
                variant="secondary"
                onClick={randomlyAssignParticipants}
              >
                {selectedParticipantsInMainRoom.length > 0
                  ? t('breakouts.assignSelectedEvenly')
                  : t('breakouts.assignAllEvenly')}
              </Button>
            )}
          </Stack>
        </CardContent>
      </div>
      <style jsx>{`
        section.drop-hover {
          outline: 1px solid var(--focus-border);
        }

        .room-container {
          background-color: var(--card-grey-bg);
          border-radius: 4px;
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          padding: 8px;
          flex-direction: column;
        }

        .participants-container {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          max-width: 100%;
          position: relative;
        }

        .header-container {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
        }

        .header-container :global(h1) {
          margin-bottom: 12px;
        }

        .buttons-container {
          display: flex;
          margin-bottom: 12px;
        }

        .select-buttons {
          display: flex;
          flex-grow: 1;
          flex-wrap: wrap;
          gap: 8px;
        }

        .room-container :global(.participants-left) {
          align-items: center;
          display: flex;
          padding: 2px;
        }
      `}</style>
    </Card>
  );
};
